import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import Preloader from "../../../core/common/loader/preLoader";
import { DateFormat } from "../../../core/hooks/dateFormat";
import Api from "../../../core/data/redux/utils/apiCall";
import { all_routes } from "../../router/all_routes";
import PermissionGuard from "../../permissionGuard";
import { isSupportViewer } from "../../../utils/permissionUtils";
import {
  fetchTypeModels,
  generateTypeIdFromText,
  listMasterRecords,
  MASTER_TYPES,
  MasterRecord,
  OTHER_TYPE,
  saveMasterRecord,
  toggleMasterStatus,
  TYPE_ID_MIN_LEN,
} from "./eAttestation.service";

const routes = all_routes;
const PRIMARY = "#c0282c";

const EAttestationMaster: React.FC = () => {
  const [form] = Form.useForm();
  const [records, setRecords] = useState<MasterRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MasterRecord | null>(null);
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [togglingIds, setTogglingIds] = useState<Record<number, boolean>>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [customType, setCustomType] = useState("");
  const [apiTypes, setApiTypes] = useState<string[]>([...MASTER_TYPES]);
  const selectedType = Form.useWatch("type", form);
  const isOtherType = selectedType === OTHER_TYPE;

  const fetchRecords = useCallback(async (type = typeFilter) => {
    setLoading(true);
    try {
      const list = await listMasterRecords({ type: type || "" });
      setRecords(Array.isArray(list) ? list : []);
    } catch (error: any) {
      toast.error(Api.getErrorMessage(error) || "Failed to load master records");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchRecords("");
  }, []);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const list = await fetchTypeModels();
        if (list.length) setApiTypes(list);
      } catch {
        /* keep fallback MASTER_TYPES */
      }
    };
    loadTypes();
  }, []);

  const typeOptions = useMemo(() => {
    const fromApi = records
      .map((row) => String(row.type || "").trim())
      .filter(Boolean);
    const merged = Array.from(new Set([...apiTypes, ...fromApi, OTHER_TYPE]));
    if (customType && !merged.includes(customType)) {
      merged.push(customType);
    }
    return merged.sort();
  }, [records, customType, apiTypes]);

  const filteredRecords = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return records.filter((row) => {
      if (statusFilter === "active" && !row.is_active) return false;
      if (statusFilter === "inactive" && row.is_active) return false;
      if (!query) return true;
      return [row.type, row.type_id, row.type_text]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query));
    });
  }, [records, searchText, statusFilter]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: filteredRecords.length,
    }));
  }, [filteredRecords.length, searchText, statusFilter, typeFilter]);

  const openCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      type: undefined,
      otherType: "",
      type_text: "",
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record: MasterRecord) => {
    setEditingRecord(record);
    const knownTypes = apiTypes.filter((type) => type !== OTHER_TYPE);
    const isKnownType = knownTypes.includes(String(record.type || "").trim());
    form.setFieldsValue({
      type: isKnownType ? record.type : OTHER_TYPE,
      otherType: isKnownType ? "" : record.type,
      type_text: record.type_text,
      is_active: !!record.is_active,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const typeText = String(values.type_text || "").trim();
      const typeId =
        editingRecord &&
        typeText === String(editingRecord.type_text || "").trim() &&
        editingRecord.type_id
          ? String(editingRecord.type_id).trim().toUpperCase()
          : generateTypeIdFromText(typeText);

      if (!typeId || typeId.length < TYPE_ID_MIN_LEN) {
        toast.error("Type text must generate a Type ID of at least 2 characters");
        return;
      }

      const specifiedType = String(
        values.otherType ?? form.getFieldValue("otherType") ?? ""
      ).trim();
      const typeValue =
        values.type === OTHER_TYPE
          ? specifiedType
          : String(values.type || "").trim();

      if (values.type === OTHER_TYPE && !specifiedType) {
        form.setFields([
          { name: "otherType", errors: ["Please enter the type"] },
        ]);
        return;
      }

      const payload: Partial<MasterRecord> = {
        type: typeValue,
        type_id: typeId,
        type_text: typeText,
        is_active: values.is_active !== false,
      };
      if (editingRecord?.id) payload.id = editingRecord.id;

      setSaving(true);
      const result = await saveMasterRecord(payload);
      if (result.success) {
        toast.success(result.message);
        closeModal();
        await fetchRecords(typeFilter);
      } else {
        toast.error(result.message || "Unable to save master record");
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      toast.error(Api.getErrorMessage(error) || "Unable to save master record");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (record: MasterRecord, checked: boolean) => {
    if (!record.id || isSupportViewer()) return;
    setTogglingIds((prev) => ({ ...prev, [record.id as number]: true }));
    setRecords((prev) =>
      prev.map((row) =>
        row.id === record.id ? { ...row, is_active: checked } : row
      )
    );
    try {
      const result = await toggleMasterStatus(record.id, checked);
      if (result.success) {
        toast.success(result.message);
        if (result.data) {
          setRecords((prev) =>
            prev.map((row) =>
              row.id === record.id ? { ...row, ...result.data } : row
            )
          );
        }
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      setRecords((prev) =>
        prev.map((row) =>
          row.id === record.id ? { ...row, is_active: record.is_active } : row
        )
      );
      toast.error(Api.getErrorMessage(error) || "Failed to update status");
    } finally {
      setTogglingIds((prev) => {
        const next = { ...prev };
        delete next[record.id as number];
        return next;
      });
    }
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (_: any, __: MasterRecord, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Type",
      dataIndex: "type",
      sorter: (a: MasterRecord, b: MasterRecord) =>
        String(a.type || "").localeCompare(String(b.type || "")),
    },
    {
      title: "Type Text",
      dataIndex: "type_text",
      sorter: (a: MasterRecord, b: MasterRecord) =>
        String(a.type_text || "").localeCompare(String(b.type_text || "")),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 140,
      render: (_: boolean, record: MasterRecord) => (
        <Switch
          checked={!!record.is_active}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          loading={!!togglingIds[record.id as number]}
          onChange={(checked) => handleToggle(record, checked)}
          disabled={isSupportViewer()}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (value: string) => DateFormat(value) || "-",
      sorter: (a: MasterRecord, b: MasterRecord) =>
        new Date(a.created_at || 0).getTime() -
        new Date(b.created_at || 0).getTime(),
    },
    ...(!isSupportViewer()
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            width: 90,
            render: (_: any, record: MasterRecord) => (
              <Space>
                <PermissionGuard route="e-attestation/master">
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => openEdit(record)}
                  />
                </PermissionGuard>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="page-wrapper">
      {loading && !records.length ? <Preloader /> : null}
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">E-Attestation Master</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Administration</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  E-Attestation Master
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <PermissionGuard route="e-attestation/master">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreate}
                style={{
                  backgroundColor: PRIMARY,
                  border: `1px solid ${PRIMARY}`,
                  fontWeight: 500,
                }}
              >
                Add Master Record
              </Button>
            </PermissionGuard>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <div className="px-3 pb-2 d-flex flex-wrap gap-2">
              <Select
                allowClear
                placeholder="Filter by type"
                value={typeFilter || undefined}
                style={{ minWidth: 200 }}
                options={typeOptions
                  .filter((type) => type !== OTHER_TYPE)
                  .map((type) => ({
                    value: type,
                    label: type,
                  }))}
                onChange={(value) => {
                  const next = value || "";
                  setTypeFilter(next);
                  fetchRecords(next);
                }}
              />
              <Select
                value={statusFilter}
                style={{ minWidth: 150 }}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                onChange={setStatusFilter}
              />
            </div>
            <Table
              columns={columns}
              dataSource={filteredRecords}
              Selection={false}
              paginationConfig={{
                ...pagination,
                total: filteredRecords.length,
              }}
              handlePagination={(next: any) =>
                setPagination((prev) => ({
                  ...prev,
                  current: next.current || 1,
                  pageSize: next.pageSize || prev.pageSize,
                  total: filteredRecords.length,
                }))
              }
              loading={loading}
              handleSearch={setSearchText}
              searchValue={searchText}
              searchPlaceholder="Search type or type text"
            />
          </div>
        </div>

        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <i className={`ti ti-${editingRecord ? "edit" : "plus"}`} />
              {editingRecord ? "Edit Master Record" : "Add Master Record"}
            </div>
          }
          open={modalOpen}
          onOk={handleSave}
          onCancel={closeModal}
          confirmLoading={saving}
          okText={editingRecord ? "Update" : "Create"}
          cancelText="Cancel"
          destroyOnClose
          width={640}
        >
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="type"
                  label="Type"
                  rules={[{ required: true, message: "Please select type" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select type"
                    optionFilterProp="label"
                    onSearch={setCustomType}
                    onChange={(value) => {
                      if (value !== OTHER_TYPE) {
                        form.setFieldsValue({ otherType: "" });
                      }
                    }}
                    options={typeOptions.map((type) => ({
                      value: type,
                      label:
                        customType &&
                        type === customType &&
                        !(apiTypes as readonly string[]).includes(type) &&
                        type !== OTHER_TYPE
                          ? `Use "${type}"`
                          : type,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={24} style={{ display: isOtherType ? "block" : "none" }}>
                <Form.Item
                  name="otherType"
                  label="Specify Type"
                  preserve
                  rules={
                    isOtherType
                      ? [
                          { required: true, message: "Please enter the type" },
                          { min: 2, message: "Type must be at least 2 characters" },
                          {
                            validator: async (_, value) => {
                              const text = String(value || "").trim();
                              if (!text) {
                                return Promise.reject(new Error("Please enter the type"));
                              }
                              if (text.toLowerCase() === OTHER_TYPE) {
                                return Promise.reject(
                                  new Error("Please enter a custom type, not other")
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]
                      : []
                  }
                >
                  <Input placeholder="Enter custom type" maxLength={50} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="type_text"
                  label="Type Text"
                  rules={[
                    { required: true, message: "Please enter type text" },
                    { min: 2, message: "Type text must be at least 2 characters" },
                  ]}
                >
                  <Input
                    placeholder="e.g. Visually Handicapped"
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="is_active"
                  label="Status"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default EAttestationMaster;
